import React, { useState, useEffect, useMemo, useContext, useCallback, FC } from 'react';
import axios from "axios";
import { Typography, useSnackbar, ThemeProvider, Paper, DataTable, Breadcrumb, BreadcrumbItem, Box } from '@pixel/core';
import ListBase, {
    ListAppliedSearchFilterProps,
    ListSearchChangeProps,
    SortOrderProps,
    ListSearchFilterSelectMultiProps,
    ListSearchFilterSelectSingleProps,
    OnSelectAllChangeEvent
} from '@pixel/core/ListBase';
import Tooltip from '@pixel/core/Tooltip';
import Link from '@pixel/core/Link';
import CircularProgress from '@pixel/core/CircularProgress';
import { createInternalTheme } from "@pixel/internal/styles";
import { LayoutRoot, LayoutRootSection } from '@pixel/internal';
import TableChartIcon from '@pixel/assets/icons/TableChart';
import { useMultipleLocalizedText } from '../../../../contexts/Localization/localizationHooks';
import { 
    IBreadcrumbHierarchyObject, 
    IAccessGroupLocationsData, 
    ILocationsGetParms, 
    ILocationsResultData, 
    ILocationsSearchGetParms,
    IDimensionsProps,
    AssociationType,
    IStoredMappingData,
    IDimensionsAPIPayload,
    ISelectedDimensionsData,
 } from '../../OdsTypes';
import OdsContext from '../../OdsContext';
import { 
    isAvailableInBreadcrumb,
    transformLocationsResult, 
    updateBreadcrumb, 
    setLocalStorageDimensionData, 
    updateDimensionLocalMapList, 
    getPartiallySelectedModifiedIds,
    getFullySelectedKeys, 
    getLocalStorageDimensionData, 
    updateLocalSelectionState,
    getSelectedSubDimensionsCount,
    getPartiallySelectedDimensionIds,
    mergePreselectedAndActiveDimensions,
    getDynamicLengthForDimensions,
    handleSelectAllDimensions,
} from '../../OdsTransform';
import { SelectChangeEvent } from '@mui/material';
import { AddDimensionModalBreadcrumb } from '../modal/AddDimensionModalBreadcrumb';

const Locations: FC<IDimensionsProps> = ({
    accessGroupData,
    selectedLocationForAPIPayload,
    setSelectedLocationForAPIPayload,
    setCurrentDimensionSelectionList,
}) => {

    const OrgElementsContext = useContext(OdsContext);
    const {
        ODS_SELECTED,
        ODS_TAB_LOCATIONS,
        ODS_SUB_LOCATIONS,
        ODS_SEARCH_PLACEHOLDER,
        ODS_SEARCH_BUSINESS_UNITS,
        ODS_ROWS_PER_PAGE_LABEL,
        I18N_NO_RESULTS_FOUND,
        ODS_LOCATION_PARENT,
    } = useMultipleLocalizedText();
    const theme = createInternalTheme({ darkMode: false, theme: {} });

    /*** styling */
    const jvLocationTitle = {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        wordBreak: 'break-all',
        whiteSpace: 'nowrap',
        display: 'inline-block',
    }

    const overlayStyling = {
        display: 'flex',
        justifyContent: 'center',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        alignItems: 'center',
        zIndex: '1',
    }
    /*** End styling */

    const ODSPageSizeSessionStorage = () => JSON.parse(sessionStorage.getItem("ODS_PageSize")) ? JSON.parse(sessionStorage.getItem("ODS_PageSize")) : OrgElementsContext.defaultPageSize;
    const snackbar = useSnackbar();
    const partiallySelectedLocationIds = getPartiallySelectedDimensionIds('AG_location_list');

    /** memoized */
    const locationFetchParams: ILocationsGetParms = useMemo(() => ({
        pageSize: ODSPageSizeSessionStorage(),
        pageNumber: 0,
        parentEId: null,
    }), []);
    const LocationSearchParams: ILocationsGetParms = useMemo(() => ({
        pageSize: ODSPageSizeSessionStorage(),
        pageNumber: 0,
        keywords: '',
    }), []);
    /** end memoized */

    /*** state variables  */
    const [locFetchPayload, setLocFetchPayload] = useState<ILocationsGetParms>(locationFetchParams);
    const [locSearchPayload, setLocSearchPayload] = useState<ILocationsSearchGetParms>(LocationSearchParams);
    const [results, setResults] = useState<ILocationsResultData>({ nodes: [], totalCount: 0, pageNumber: 0, pageSize: ODSPageSizeSessionStorage() });
    const [locHierarchyTree, setLocHierarchyTree] = useState([{ name: 'All', eid: '' }]);
    const [selectedLocations, setSelectedLocations] = useState<Array<IAccessGroupLocationsData['eid']>>([]);
    const [perPageSize, setPerPageSize] = useState(ODSPageSizeSessionStorage());
    const [listLoading, setListLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [noSearchResult, setNoSearchResult] = useState(false);
    const [showInProgress, setShowInProgress] = useState(false);
    const [pagingContext, setPagingContext] = useState({
        hideFirstLastActions: true,
        perPage: perPageSize,
        page: 1,
        first: 1,
        last: perPageSize,
    });
    /*** end state variables */

    /*** use effects */
    useEffect(() => {
        fetchLocationList(locFetchPayload, accessGroupData ? accessGroupData.eid : null);
        setSearchText('');
    }, [locFetchPayload]);

    useEffect(() => {
        if (searchText && searchText.length > 1) {
            const accessGroupEid = accessGroupData ? accessGroupData.eid : null;
            const modifiedPartiallySelectedIds : Array<string> = getPartiallySelectedModifiedIds(partiallySelectedLocationIds, 'AG_location_list');
            const bodyPayload = {
                modifiedPartiallySelectedEIds: modifiedPartiallySelectedIds
            }
            axios.post(window.jvUrls.admin + '/action/accessGroups/locations/search/' + accessGroupEid, bodyPayload,{
                params: locSearchPayload
            })
                .then(function (response) {
                    let activeLocations = response.data.activeLocations;
                    setResults(transformLocationsResult(activeLocations));
                    setSelectedLocations(updateDimensionLocalMapList(response?.data?.associatedLocationsArray, 'AG_location_list', response.data.childrenForModifiedPartiallySelectedRequestedIds));
                    mergePreselectedAndActiveDimensions(activeLocations?.locations, 'namedLocationsData');
                    setCurrentDimensionSelectionList(getLocalStorageDimensionData('AG_location_list'));
                    setPagingContext({
                        hideFirstLastActions: true,
                        perPage: activeLocations.pageSize,
                        page: activeLocations.pageNumber + 1,
                        first: activeLocations.locations.length > 0 ? activeLocations.pageNumber * activeLocations.pageSize + 1 : 0,
                        last: activeLocations.pageSize * activeLocations.pageNumber + activeLocations.locations.length,
                    });
                    setListLoading(false);
                    activeLocations?.totalCount === 0 ? setNoSearchResult(true) : setNoSearchResult(false);
                })
                .catch(function (error) {
                    console.log(error);
                    setListLoading(false);
                });
        }
    }, [searchText, locSearchPayload]);
    /*** End use effects */

    /*** xhr side effects */
    const fetchLocationList = async (locFetchPayload: ILocationsGetParms, accessGroupEid: string) => {
        try {
            setListLoading(true);
            const modifiedPartiallySelectedIds : Array<string> = getPartiallySelectedModifiedIds(partiallySelectedLocationIds, 'AG_location_list');
            const bodyPayload = {
                modifiedPartiallySelectedEIds: modifiedPartiallySelectedIds
            }
            await axios.post(window.jvUrls.admin + '/action/accessGroups/locations/' + accessGroupEid, bodyPayload, {
                params: locFetchPayload
            })
                .then( (response) => {
                    let activeLocations = response.data.activeLocations;
                    setResults(transformLocationsResult(activeLocations));
                    setSelectedLocations(updateDimensionLocalMapList(response.data.associatedLocationsArray, 'AG_location_list', response.data.childrenForModifiedPartiallySelectedRequestedIds));
                  

                    mergePreselectedAndActiveDimensions(activeLocations?.locations, 'namedLocationsData');
                    setCurrentDimensionSelectionList(getLocalStorageDimensionData('AG_location_list'));
                    if (locHierarchyTree.length === 1) {
                        sessionStorage.setItem("locationSearchFilter", JSON.stringify(response.data.activeLocations.locations[0]));
                    }
                    activeLocations.locations.length > 0 && setNoSearchResult(false);
                    setPagingContext({
                        hideFirstLastActions: true,
                        perPage: activeLocations.pageSize,
                        page: activeLocations.pageNumber + 1,
                        first: activeLocations.locations.length > 0 ? activeLocations.pageNumber * activeLocations.pageSize + 1 : 0,
                        last: activeLocations.pageSize * activeLocations.pageNumber + activeLocations.locations.length,
                    });
                    setListLoading(false);
                })
                .catch((error) => {
                    // show snackbar here
                    setListLoading(false);
                });
        } catch (error) {
            // show snackbar here
            console.log(error);
        }
    }

    
    const updatePayload = (location: IBreadcrumbHierarchyObject) => {
        if (location.eid === '') {
            setLocFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
        } else {
            setLocFetchPayload({ ...locSearchPayload, parentEId: location.eid, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
        }
    }
    /*** end xhr side effects */

    /*** helper functions */
    const clearLocalStorageAndState = () => {
        localStorage.removeItem('locationSearchFilter');
        setSearchText('');
    };

    const handleKeywordSearch = (props: ListSearchChangeProps) => {
        setSearchText(props.q);
        setLocSearchPayload({
            ...locSearchPayload,
            pageSize: ODSPageSizeSessionStorage(),
            pageNumber: 0,
            keywords: encodeURIComponent(props.q)
        });
        setListLoading(true);
        localStorage.setItem('locationSearchFilter', JSON.stringify({ q: props.q }));
    }
    /*** end helper functions */

    /*** event handlers */
    const onBreadcrumChange = (location: IBreadcrumbHierarchyObject) => {
        setLocHierarchyTree(updateBreadcrumb(locHierarchyTree, location));
        updatePayload(location);
    }

    const resetSearch = () => {
        if (searchText !== '') {
            setLocHierarchyTree([{ name: 'All', eid: '' }]);
            setLocFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
        }
    }

    const handleSearch = (props: ListSearchChangeProps) => {
        setLocHierarchyTree([{ name: 'All', eid: '' }]);
        setSearchText(props.q);
        if (props.q.length > 1) {
            setLocSearchPayload({ ...locSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: encodeURIComponent(props.q) })
            setListLoading(true);
        } else {
            setNoSearchResult(false);
            resetSearch();
        }
    };

    const onPageChanged = (event: any, currPage: number) => {
        if (searchText.length > 1) {
            setLocSearchPayload({ ...locSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1 });
        } else {
            if (locHierarchyTree[locHierarchyTree.length - 1].eid === '') {
                setLocFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1 });
            } else {
                setLocFetchPayload({ ...locFetchPayload, pageSize: ODSPageSizeSessionStorage(), parentEId: locHierarchyTree[locHierarchyTree.length - 1].eid, pageNumber: currPage - 1 });
            }
        }
    }

    const onPaginationChange = (event: SelectChangeEvent) => {
        sessionStorage.setItem('ODS_PageSize', JSON.stringify(Number(event.target.value)));
        setPerPageSize(Number(event.target.value));
        if (searchText !== '') {
            setLocSearchPayload({ ...locSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: searchText })
            setListLoading(true);
        } else {
            updatePayload(locHierarchyTree[locHierarchyTree.length - 1]);
        }
    }

    const onHandleLocClick = (location: IBreadcrumbHierarchyObject) => {
        const newEntry = { name: location.name, eid: location.eid };
        clearLocalStorageAndState();
        !isAvailableInBreadcrumb(locHierarchyTree, location) && setLocHierarchyTree((locHierarchyTree) => [...locHierarchyTree, newEntry]);
        setLocFetchPayload({ ...locFetchPayload, parentEId: location.eid, pageNumber: 0, pageSize: ODSPageSizeSessionStorage() });
    }

    const onHandleSearchedLocClick = (location: IBreadcrumbHierarchyObject, LocTree: Array<IBreadcrumbHierarchyObject>) => {
        let newEntry;
        if (LocTree) {
            let index = LocTree.findIndex(loc => loc.eid === location.eid);
            let LocBreadcrumb = LocTree.slice(0, index + 1);
            newEntry = [{ name: 'All', eid: '' }, ...LocBreadcrumb];
        } else {
            newEntry = [{ name: 'All', eid: '' }, { name: location.name, eid: location.eid }];
        }
        clearLocalStorageAndState();
        setLocHierarchyTree(newEntry);
        setLocFetchPayload({ ...locFetchPayload, parentEId: location.eid, pageNumber: 0, pageSize: ODSPageSizeSessionStorage() });
    }

    // /*** use effects */
    useEffect(() => {
        const modifiedSelectedLocations: Array<ISelectedDimensionsData> = [];
        selectedLocations?.forEach((value: string) => {
            modifiedSelectedLocations.push({eid: value, associationType: AssociationType.FULLY_ASSOCIATED});
        });
        setSelectedLocationForAPIPayload(modifiedSelectedLocations);
        selectedLocations?.length === 0 && setSelectedLocationForAPIPayload([]);
    }, [selectedLocations]);

    /**
    * handleLocationSelection Manipulates the click event on checkbox
    *
    * @param {IAccessGroupLocationsData} node - list item node
    * @param {boolean} nextSelectedState - if its a check or uncheck event
    */
    const handleLocationSelection = useCallback((node: IAccessGroupLocationsData, nextSelectedState: boolean) => {
        const prevAssociatedData: IStoredMappingData = getLocalStorageDimensionData('AG_location_list');
        
        if (nextSelectedState) {
            setSelectedLocations([...selectedLocations, node.eid]);
        } else {
            setSelectedLocations(selectedLocations.filter(item => item !== node.eid));
        }
        setSelectedLocations(updateLocalSelectionState(node.eid, nextSelectedState, 'AG_location_list', prevAssociatedData));
    }, [selectedLocations]);

    /**
    * handleLocationSelectAll Manipulates the click event to selectAll list items
    *
    * @param {any} event - event object
    * @param {any} eventType - eventType - selectAll, unselectAll, etc
    * @param {any} context - context of the event
    */
    const handleLocationSelectAll: OnSelectAllChangeEvent = (event: any, eventType: any, context: any) => {
        const prevLocalList: IStoredMappingData = getLocalStorageDimensionData('AG_location_list');
        const localList: IStoredMappingData = handleSelectAllDimensions(eventType, 'AG_location_list', prevLocalList);
        
        setLocalStorageDimensionData('AG_location_list', localList);
        setSelectedLocations(getFullySelectedKeys([localList?.dimensionIdToDimensionList]));
    }

    /*** render helpers */
    const populateTitle = (node: IAccessGroupLocationsData) => {
        if (node.subChildrenCount > 0) {
            return <Tooltip title={node.name} arrow placement="bottom"><Link underline="hover" sx={{ ...jvLocationTitle, maxWidth: '100%', fontWeight: '600' }} onClick={() => (node.ancestors && searchText.length > 1) ? onHandleSearchedLocClick({ name: node.name, eid: node.eid }, [...node.ancestors, { name: node.name, eid: node.eid }]) : onHandleLocClick(node)} variant="body2">{node.name}</Link></Tooltip>;
        } else {
            return <Tooltip title={node.name} arrow placement="bottom"><span><Typography variant="body2" sx={{ ...jvLocationTitle, maxWidth: '100%' }} >{node.name}</Typography></span></Tooltip>;
        }
    }

    const renderParentLoc = (node: IAccessGroupLocationsData) => {
        const list: any = [];
        if (node.ancestors && node.ancestors.length > 0) {
            node.ancestors.map((parent, index) => {
                list.push(
                    <span style={{ display: 'inline-flex' }}>
                        <React.Fragment>
                            <Tooltip key={parent.eid} title={parent.name} arrow placement="bottom"><Link underline="hover" className='jv-location-title jv-location-title-link' sx={{ maxWidth: '120px', fontWeight: '600' }} onClick={() => onHandleSearchedLocClick({ name: parent.name, eid: parent.eid }, node.ancestors)} variant="body2">{parent.name}</Link></Tooltip>
                            <span style={{ padding: '0 6px' }}>{index < node.ancestors.length - 1 && ' /   '}</span>
                        </React.Fragment>
                    </span>
                );
            })
        }
        return list;
    }

    /*** end render helpers */

    const columns = useMemo(() => [
        { id: 'locations', columnName: ODS_TAB_LOCATIONS, maxWidth: 250, renderCell: (node: IAccessGroupLocationsData) => populateTitle(node) },
        { id: 'SubLocationsSelected', columnName: `${ODS_SUB_LOCATIONS || ''} ${ODS_SELECTED || ''}`, renderCell: (node: ISelectedDimensionsData) => getSelectedSubDimensionsCount(node?.eid, 'AG_location_list')
        , minWidth: 80 },
        { id: 'sublocations', columnName: ODS_SUB_LOCATIONS || '', renderCell: (node: IAccessGroupLocationsData) => node.subChildrenCount, minWidth: 80 },
        ...(searchText.length > 1 ? [{ id: 'ParentLocation', columnName: ODS_LOCATION_PARENT || '', renderCell: (node: IAccessGroupLocationsData) => renderParentLoc(node), maxWidth: 160 }] : []),
    ], []);

    return (
        <React.StrictMode>
            <ThemeProvider theme={theme}>
                <LayoutRoot>
                    <LayoutRootSection top bottom transparent className='jv-tab-panel-wrapper'>
                        <AddDimensionModalBreadcrumb
                            DimensionHierarchyTree={locHierarchyTree}
                            onBreadcrumChange={onBreadcrumChange}
                            searchText={searchText}
                        />
                        {(showInProgress && <Box sx={overlayStyling}><CircularProgress size={60} /></Box>)}
                        <Box className='content-wrapper'>
                            <ListBase
                                className
                                showSearch
                                labelSearch={ODS_SEARCH_PLACEHOLDER}
                                labelSearchAria={ODS_SEARCH_BUSINESS_UNITS}
                                // @ts-ignore
                                results={results} // has the pager content
                                selectable
                                selectedIds={selectedLocations}
                                partiallySelectedIds={partiallySelectedLocationIds}
                                onSelection={handleLocationSelection}
                                onSelectAll={locHierarchyTree?.length === 1 && handleLocationSelectAll}
                                allSelected={getDynamicLengthForDimensions(selectedLocations, results?.nodes) === results?.nodes?.length}
                                onSearch={handleSearch}
                                appliedSearchQuery={searchText}
                                pagingContext={pagingContext}
                                onPageChanged={onPageChanged}
                                onPaginationChange={(event: SelectChangeEvent) => onPaginationChange(event)}
                                rowsPerPageLabel={ODS_ROWS_PER_PAGE_LABEL}
                                resultsPerPage={perPageSize}
                                perPageOptions={OrgElementsContext.perPageSelectOptions}
                                viewContexts={[
                                    {
                                        ariaLabel: 'Datatable View',
                                        icon: <TableChartIcon />,
                                        renderResults: (viewContextProps) => {
                                            if (listLoading) {
                                                return (<div className={'bu-list-loader'} style={{ display: 'flex', justifyContent: 'center' }}>
                                                    <CircularProgress size={34} />
                                                </div>)
                                            } else {
                                                if (!noSearchResult) {
                                                    return (
                                                        <DataTable
                                                            {...viewContextProps}
                                                            getRowID={node => node.eid}
                                                            columns={columns}
                                                            labelCheckboxAriaLabel={ODS_SELECTED || ''}
                                                            labelCheckboxKey='eid'
                                                        />
                                                    )
                                                } else {
                                                    return (<div className={'bu-list-no-results'}><Paper sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}> {I18N_NO_RESULTS_FOUND}</Paper></div>)
                                                }
                                            }
                                        }
                                    }
                                ]}
                            />
                        </Box>
                    </LayoutRootSection>
                </LayoutRoot>
            </ThemeProvider>
        </React.StrictMode>
    )
}

export default Locations;
